import { useCallback, useState } from "react";

import { toast } from "@/shared/hooks/use-toast";

import { acqService, type AcqFeedbackCode, type AcqOpportunityCard } from "../api/acqService";
import { actionSpecFor, FEEDBACK_LABEL } from "./acqLabels";
import { errorToast } from "@/shared/lib/apiErrors";

/**
 * 商机卡上的四个写动作(接手 / 补全联系方式 / 五按钮反馈 / 主按钮推进状态)。
 * 今日商机与线索库共用一份 —— 两个页面各抄一遍就会出现"一边推进了状态、另一边没推"。
 *
 * ★ 服务端返回**整张更新后的卡**,前端就地替换那一条,不重拉整页:
 *   重拉会让今日商机重排(刚点过的那条可能掉出前 5),用户会以为点丢了。
 * ★★ 四个里只有 `unlock` 会花钱。**每一句提示都要把"扣没扣"说明白** ——
 *   一个用户搞不清什么时候会花钱,结果不是"他会少点",是"他对所有按钮都不敢点"。
 */
export function useAcqActions(patch: (card: AcqOpportunityCard) => void) {
  const [busyId, setBusyId] = useState<string | null>(null);

  /** `cardOf` 把响应体里的卡剥出来:接手/补全那两口外面还包着一层信封,其余是裸卡。 */
  const run = useCallback(
    async function <T>(
      id: string,
      work: () => Promise<T>,
      cardOf: (r: T) => AcqOpportunityCard,
      fail: string
    ): Promise<T | null> {
      setBusyId(id);
      try {
        const res = await work();
        patch(cardOf(res));
        return res;
      } catch (e) {
        toast(errorToast(e, { fallbackTitle: fail }));
        return null;
      } finally {
        setBusyId(null);
      }
    },
    [patch]
  );

  /**
   * 接手一条(**免费**,契约 §12.4)。ai 出产物 → 卡上展开产物面板 + 状态推进。
   * ★ 这是 v2 主按钮的落点:用户**先白拿到一份能直接用的东西**,再决定要不要为
   *   「补全联系方式」花钱。反过来(先收钱再给东西)正是上一版把人吓住的形状。
   */
  const take = useCallback(
    async (id: string) => {
      const res = await run(
        id,
        () => acqService.take(id),
        (r) => r.card,
        "接手没成功，没扣分"
      );
      if (res)
        toast({
          title: "已接手",
          description: "产物在卡上，可以直接复制走；之后在「线索库」跟进，不再占今日名额",
        });
    },
    [run]
  );

  const unlock = useCallback(
    async (id: string) => {
      const res = await run(
        id,
        () => acqService.unlock(id),
        (r) => r.card,
        "补全没跑成，积分未扣"
      );
      if (!res) return;
      // ★★ 三种结局,三句话,**一句都不能合并**:
      //   · found=false → 公网上查不到。**没找到不收钱**(契约 §12.10)。说清楚了,
      //     用户才不会以为自己白花了一笔;含糊过去他下次就不敢点了。
      //   · charged=false 且 found → 之前补过,幂等没再扣。这时弹「已扣 N 积分」
      //     是在吓唬一个其实没花钱的用户。
      //   · charged=true → 真扣了,数用服务端的 `credits`,**永不硬编 50**。
      if (!res.found) {
        toast({
          title: "没找到公开的联系方式",
          description: "这次没扣分。可以先用产物从公开渠道接触。",
        });
        return;
      }
      toast(
        res.charged
          ? { title: "联系方式已补全", description: `本次扣 ${res.credits} 积分` }
          : { title: "联系方式已补全", description: "这条之前补过，没有重复扣分" }
      );
    },
    [run]
  );

  const feedback = useCallback(
    async (id: string, code: AcqFeedbackCode) => {
      const ok = await run(
        id,
        () => acqService.feedback(id, code),
        (c) => c,
        "反馈没记上"
      );
      if (ok)
        toast({ title: `已标记「${FEEDBACK_LABEL[code]}」`, description: "明天的单子会照这个调" });
    },
    [run]
  );

  /**
   * 分派给某人 / 取消分派(契约 §13.4)。管理员与主管才调得动,门在服务端。
   * ★ 失败要说出来:一次没派成而面上看着派成了,结果是两个人都以为对方在跟这条。
   */
  const assign = useCallback(
    async (id: string, assigneeId: number | null, name?: string) => {
      const ok = await run(
        id,
        () => acqService.assign(id, assigneeId),
        (c) => c,
        "没分派成功"
      );
      if (ok) {
        toast(
          assigneeId == null
            ? { title: "已取消分派", description: "这条回到公共池，谁都能认领" }
            : { title: `已分派给 ${name ?? ok.assigneeName ?? "对方"}` }
        );
      }
    },
    [run]
  );

  /**
   * 坐席认领(契约 §13.4)。**只有未分派的能认**;已分派给别人后端回 409。
   * ★ 409 的提示要说"已经是别人的了",不能笼统说"认领失败" —— 前者告诉他去看下一条,
   *   后者会让他以为是网络问题,然后一直点。
   */
  const claim = useCallback(
    async (id: string) => {
      const ok = await run(
        id,
        () => acqService.claim(id),
        (c) => c,
        "没认领到 —— 多半是刚被别人认走了"
      );
      if (ok) toast({ title: "已认领", description: "这条归你跟了，在「只看我的」里找得到" });
    },
    [run]
  );

  /** 主按钮:把这条推进到该动作对应的状态(契约 §2 允许跳级)。 */
  const takeAction = useCallback(
    async (card: AcqOpportunityCard) => {
      if (!card.inference) return;
      const next = actionSpecFor(card.inference.suggestedAction).nextState;
      if (card.state === next) return;
      await run(
        card.id,
        () => acqService.setState(card.id, next),
        (c) => c,
        "状态没更新"
      );
    },
    [run]
  );

  return { busyId, take, unlock, feedback, takeAction, assign, claim };
}
